import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { API_URLS } from '../../config/api';
import {
  Briefcase,
  ArrowRight,
  Layers,
  FolderKanban,
  Sparkles,
} from 'lucide-react';

const gradientByIndex = [
  'from-blue-500 to-cyan-500',
  'from-violet-500 to-purple-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-500',
  'from-rose-500 to-pink-600',
  'from-indigo-500 to-blue-600',
];

const ProjectCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setContentReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [catRes, projRes] = await Promise.all([
          fetch(API_URLS.PROJECT_CATEGORIES),
          fetch(API_URLS.PROJECTS),
        ]);
        const catData = await catRes.json();
        const projData = await projRes.json();

        if (Array.isArray(catData)) {
          setCategories(catData.map(c => ({
            ...c,
            subcategories: Array.isArray(c.subcategories) ? c.subcategories : (c.subcategories ? JSON.parse(c.subcategories || '[]') : []),
            features: Array.isArray(c.features) ? c.features : (c.features ? JSON.parse(c.features || '[]') : []),
          })));
        } else {
          setCategories([]);
        }
        setProjects(Array.isArray(projData) ? projData : (projData?.data || []));
      } catch (e) {
        setCategories([]);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getProjectCount = (categoryId) =>
    projects.filter(p => String(p.category_id) === String(categoryId)).length;

  const getGradient = (cat, index) =>
    cat.color || gradientByIndex[index % gradientByIndex.length];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <div className="rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {/* Hero */}
      <section
        className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-700 text-white py-20 overflow-hidden"
        style={{
          opacity: contentReady ? 1 : 0,
          transition: 'opacity 0.5s ease-in',
        }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA2IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZykiLz48L3N2Zz4=')] opacity-100" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/projeler"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium mb-8"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Projelere dön
          </Link>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Layers className="w-4 h-4" />
            <span>Tüm kategoriler</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            Proje Kategorileri
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
            Sektör ve hizmet türüne göre projelerimizi keşfedin. İhtiyacınıza uygun kategoriden projelere göz atın.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4 text-white/90">
            <span className="flex items-center gap-2">
              <FolderKanban className="w-5 h-5" />
              {categories.length} kategori
            </span>
            <span className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              {projects.length} proje
            </span>
          </div>
        </div>
      </section>

      {/* Categories grid */}
      <div
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        style={{
          opacity: contentReady ? 1 : 0,
          transition: 'opacity 0.5s ease-in 0.15s',
        }}
      >
        {categories.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Henüz kategori bulunmuyor.</p>
            <Button asChild className="mt-4">
              <Link to="/projeler">Projelere git</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {categories.map((cat, index) => {
              const count = getProjectCount(cat.id);
              const gradient = getGradient(cat, index);
              return (
                <Card
                  key={cat.id}
                  className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white"
                >
                  <div className={`h-2 bg-gradient-to-r ${gradient}`} />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}
                      >
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <Badge variant="secondary" className="text-xs font-semibold">
                        {count} proje
                      </Badge>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {cat.name}
                    </h2>
                    {cat.description && (
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-5">
                        {cat.description}
                      </p>
                    )}
                    {!cat.description && (
                      <p className="text-gray-500 text-sm mb-5">
                        Bu kategorideki projeleri inceleyin.
                      </p>
                    )}
                    <Button
                      asChild
                      className="w-full bg-gray-900 hover:bg-blue-600 text-white"
                    >
                      <Link to={`/kategori/${encodeURIComponent(cat.slug || cat.id)}`}>
                        Projeleri gör
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-14 text-center">
          <Button asChild variant="outline" size="lg">
            <Link to="/projeler">
              Tüm projeleri listele
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCategoriesPage;
